import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, Play, Loader2 } from 'lucide-react';
import './BlogArticle.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogArticle = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBlog();
  }, [slug]);

  const fetchBlog = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setBlog(data);
      } else {
        setError('Article not found');
      }
    } catch (error) {
      setError('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="blog-article-page">
        <div className="loading-state">
          <Loader2 className="spin" size={40} />
          <p>Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="blog-article-page">
        <div className="error-state">
          <h2>Article not found</h2>
          <p>The article you're looking for doesn't exist.</p>
          <Link to="/blog" className="back-btn">
            <ArrowLeft size={18} />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-article-page" data-testid="blog-article">
      {/* Hero */}
      <section className="article-hero">
        <div className="hero-image">
          <img src={blog.cover_image} alt={blog.title} />
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <Link to="/blog" className="back-link">
              <ArrowLeft size={18} />
              Back to Blog
            </Link>
            <h1 className="article-title">{blog.title}</h1>
            <div className="article-meta">
              <span className="article-date">
                <Calendar size={16} />
                {formatDate(blog.publish_date)}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="article-content-section">
        <div className="container">
          <div className="article-body">
            {blog.video_url && (
              <div className="article-video">
                <iframe
                  src={blog.video_url.replace('watch?v=', 'embed/')}
                  title={blog.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            )}
            <div 
              className="article-text"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="article-cta">
        <div className="container">
          <div className="cta-box">
            <h3>Ready to bring your vision to life?</h3>
            <p>Book your editing slot today and experience cinematic excellence.</p>
            <Link to="/booking" className="cta-btn">Book Now</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogArticle;
